/**
 * Teams.tsx — PlatformOwner-only team management (team feature, 0049), built on the Kombi admin
 * chrome (kombi/admin/chrome.tsx) and mounted as an in-place modal over AdminHome (TeamsPanel),
 * exactly as Appointments.
 *
 * An admin builds a team: a NAME, a MANAGER (who must already hold the live Manager appointment), then
 * a roster of ordinary MEMBERS and up to two CHAMPIONS (each of whom must already hold the live
 * ChampionRole appointment). A team RECORDS these people against their appointments; it never grants a
 * role — app.appointment stays the single source of truth, and the server re-validates every named
 * manager/champion regardless of what this UI offers. The structural rules the server enforces and this
 * panel surfaces as inline errors: one team per manager, one team per user, a unique (case-insensitive)
 * name, and the 1–2-champion cap.
 *
 * Person lookup reuses the global-search suggest endpoint (fetchSuggestions), the same PersonPicker
 * pattern the Appointments panel uses. The pickers do not themselves filter to appointment-holders —
 * that check is the server's (a precise 409), surfaced here so an admin who picks an un-appointed person
 * is told exactly why rather than silently failing.
 *
 * The panel fetches its own data on open (useOverlayData → teamsLoader) and, after every mutation, calls
 * reload() to refresh in place. The registered /admin/teams route renders through a thin Teams() wrapper
 * that opens the same panel over a blank backdrop for the deep-link path.
 *
 * Provisional engineering UI — matches the other admin governance panels' posture.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { apiGet, apiPost, apiPatch, apiDelete, type TeamDetail, type TeamMemberRole } from './api';
import { fetchSuggestions } from '../search/api';
import {
  EmptyState,
  ErrorLine,
  KombiAdminOverlay,
  Notice,
  NotPermitted,
  PrimaryAction,
  ProvisionalNote,
  QuietAction,
  SectionHeader,
  Spinner,
} from '../kombi/admin/chrome';
import { useOverlayData, type ReturnFocusRef } from './overlay';

export type TeamsLoaderData =
  { state: 'signedOut' } | { state: 'forbidden' } | { state: 'ok'; teams: TeamDetail[] };

export async function teamsLoader(): Promise<TeamsLoaderData> {
  const res = await apiGet<{ teams: TeamDetail[] }>('/api/admin/teams');
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'error') {
    if (res.status === 403) return { state: 'forbidden' };
    throw new Error(`GET /api/admin/teams failed (${res.status})`);
  }
  return { state: 'ok', teams: res.body.teams };
}

/** Turn a server error code into a sentence an admin can act on. Falls back to a generic line. */
function messageForCode(code: string | null | undefined, status: number): string {
  switch (code) {
    case 'manager_not_found':
    case 'user_not_found':
      return 'That person could not be found. Try searching again.';
    case 'manager_not_appointed':
      return 'That person does not hold a live Manager appointment. Appoint them as a Manager first (Appointments), then set them as the team’s manager.';
    case 'manager_already_manages':
      return 'That person already manages another team. A person may manage only one team.';
    case 'team_name_taken':
      return 'A team with that name already exists. Choose a different name.';
    case 'already_on_team':
      return 'That person is already on a team. A person may belong to only one team — remove them from their current team first.';
    case 'is_manager':
      return 'That person is this team’s manager, so they cannot also be on its roster.';
    case 'champion_not_appointed':
      return 'That person does not hold a live ChampionRole appointment, so they cannot be a champion. Add them as a member, or appoint them first.';
    case 'too_many_champions':
      return 'This team already has the maximum of two champions.';
    default:
      return `Something went wrong (${status}). Please try again.`;
  }
}

interface PersonMatch {
  userId: string;
  name: string;
  jobTitle: string | null;
  department: string | null;
}

/**
 * Type-ahead person picker, reusing the global search field's own suggest endpoint.
 *
 * The bare `<input>` carries no class of its own: `.kombi-field input` styles it from the labelled
 * wrapper the caller supplies, so the picker cannot drift from every other field in the panel. Each
 * suggestion is a `.kombi-choice` — Kombi's own small-choice control — and deliberately carries NO
 * `aria-pressed`: these are one-shot selections, not a toggle whose state persists.
 */
function PersonPicker({
  onPick,
  selected,
  inputId,
  placeholder = 'Search for a person by name…',
}: {
  onPick: (person: PersonMatch | null) => void;
  selected: PersonMatch | null;
  inputId?: string;
  placeholder?: string;
}) {
  const [query, setQuery] = useState('');
  const [matches, setMatches] = useState<PersonMatch[]>([]);
  const [searching, setSearching] = useState(false);

  async function search(q: string) {
    setQuery(q);
    onPick(null);
    if (q.trim().length < 2) {
      setMatches([]);
      return;
    }
    setSearching(true);
    try {
      const { people } = await fetchSuggestions(q);
      setMatches(
        people.map((p) => ({
          userId: p.userId,
          name: p.name,
          jobTitle: p.jobTitle,
          department: p.department,
        })),
      );
    } finally {
      setSearching(false);
    }
  }

  if (selected) {
    return (
      <p>
        <span>
          {selected.name}
          {selected.jobTitle ? ` · ${selected.jobTitle}` : ''}
        </span>{' '}
        <QuietAction onClick={() => onPick(null)}>Change</QuietAction>
      </p>
    );
  }

  return (
    <div>
      <input
        id={inputId}
        type="text"
        value={query}
        placeholder={placeholder}
        onChange={(e) => void search(e.target.value)}
      />
      {searching && <span className="kombi-admin-pending">searching…</span>}
      {matches.length > 0 && (
        <ul className="kombi-choices">
          {matches.map((m) => (
            <li key={m.userId}>
              <button type="button" onClick={() => onPick(m)} className="kombi-choice">
                {m.name}
                {m.department ? ` — ${m.department}` : ''}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The create-team form: a name + a manager, created together. */
function CreateTeamForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [manager, setManager] = useState<PersonMatch | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create() {
    if (!manager || name.trim().length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiPost<{ ok: true; teamId: string }>('/api/admin/teams', {
        name: name.trim(),
        managerUserId: manager.userId,
      });
      if (res.kind === 'ok') {
        setName('');
        setManager(null);
        onCreated();
        return;
      }
      setError(
        res.kind === 'error'
          ? messageForCode(res.code, res.status)
          : 'Your session has ended. Close this panel and sign in again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="kombi-dialog-stack">
      <div>
        <label className="kombi-field">
          <span>Team name</span>
          <input
            id="team-name"
            name="team-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Digital &amp; IT Automation Team"
          />
        </label>
        {/* A `<div class="kombi-field">` with a `for`-linked `<label>` rather than a wrapping one (the
            CSS supports both): the picker's own markup contains buttons, and a wrapping label would
            make the nearest button its labelled control. */}
        <div className="kombi-field">
          <label htmlFor="team-manager">Manager</label>
          <PersonPicker
            selected={manager}
            onPick={setManager}
            inputId="team-manager"
            placeholder="Search for the team’s manager…"
          />
        </div>
      </div>

      {error && <ErrorLine>{error}</ErrorLine>}

      <div>
        <PrimaryAction
          onClick={() => void create()}
          disabled={busy || !manager || name.trim().length === 0}
        >
          {busy ? 'Creating…' : 'Create team'}
        </PrimaryAction>
      </div>
    </div>
  );
}

/** The add-to-roster control inside a team card: pick a person, pick Member/Champion, add. */
function AddMemberForm({ teamId, onAdded }: { teamId: string; onAdded: () => void }) {
  const [person, setPerson] = useState<PersonMatch | null>(null);
  const [role, setRole] = useState<TeamMemberRole>('Member');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function add() {
    if (!person) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiPost<{ ok: true }>(
        `/api/admin/teams/${encodeURIComponent(teamId)}/members`,
        { userId: person.userId, role },
      );
      if (res.kind === 'ok') {
        setPerson(null);
        setRole('Member');
        onAdded();
        return;
      }
      setError(
        res.kind === 'error'
          ? messageForCode(res.code, res.status)
          : 'Your session has ended. Close this panel and sign in again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="kombi-dialog-stack">
      <div>
        {/* `for`-linked, not wrapping — see CreateTeamForm. */}
        <div className="kombi-field">
          <label htmlFor={`add-person-${teamId}`}>Add a person</label>
          <PersonPicker
            selected={person}
            onPick={setPerson}
            inputId={`add-person-${teamId}`}
            placeholder="Search for a colleague…"
          />
        </div>
        <label className="kombi-field">
          <span>On the team as</span>
          <select
            id={`add-role-${teamId}`}
            value={role}
            onChange={(e) => setRole(e.target.value as TeamMemberRole)}
          >
            <option value="Member">Member</option>
            <option value="Champion">Champion (must hold the ChampionRole appointment)</option>
          </select>
        </label>
      </div>
      {error && <ErrorLine>{error}</ErrorLine>}
      <div>
        <PrimaryAction onClick={() => void add()} disabled={busy || !person}>
          {busy ? 'Adding…' : 'Add to team'}
        </PrimaryAction>
      </div>
    </div>
  );
}

/**
 * One roster row (member or champion), with a remove action. The `<li>` is bare: its rule, its spacing
 * and the `strong`/`small` type come from the `.kombi-admin-rows` list it sits in.
 *
 * The failure line is a plain `.kombi-field-error` span, NOT `ErrorLine` — this row's error has never
 * been announced (no role=alert) and giving it one now would change what a screen reader interrupts for.
 */
function RosterRow({
  teamId,
  userId,
  displayName,
  upn,
  roleLabel,
  onRemoved,
}: {
  teamId: string;
  userId: string;
  displayName: string;
  upn: string;
  roleLabel: string;
  onRemoved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      // 204 with no body → apiDelete hands back body: null (the house convention for a bodiless write).
      const res = await apiDelete<null>(
        `/api/admin/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`,
      );
      if (res.kind === 'ok') {
        onRemoved();
        return;
      }
      setError(`Could not remove (${res.kind === 'error' ? res.status : 'unauthorized'}).`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li>
      <span>
        <strong>
          {displayName} · {roleLabel}
        </strong>
        <small>{upn}</small>
      </span>
      <span className="kombi-admin-row-actions">
        {error && <span className="kombi-field-error">{error}</span>}
        <QuietAction onClick={() => void remove()} disabled={busy}>
          {busy ? 'Removing…' : 'Remove'}
        </QuietAction>
      </span>
    </li>
  );
}

/** The rename / change-manager control for one team, revealed inline under an "Edit" toggle. */
function EditTeamForm({
  team,
  onSaved,
  onCancel,
}: {
  team: TeamDetail;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(team.name);
  const [manager, setManager] = useState<PersonMatch | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameChanged = name.trim().length > 0 && name.trim() !== team.name;
  const managerChanged = manager !== null && manager.userId !== team.manager.userId;

  async function save() {
    if (!nameChanged && !managerChanged) {
      onCancel();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body: { name?: string; managerUserId?: string } = {};
      if (nameChanged) body.name = name.trim();
      if (managerChanged) body.managerUserId = manager!.userId;
      const res = await apiPatch<{ ok: true }>(
        `/api/admin/teams/${encodeURIComponent(team.teamId)}`,
        body,
      );
      if (res.kind === 'ok') {
        onSaved();
        return;
      }
      setError(
        res.kind === 'error'
          ? messageForCode(res.code, res.status)
          : 'Your session has ended. Close this panel and sign in again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="kombi-dialog-stack">
      <div>
        <label className="kombi-field">
          <span>Team name</span>
          <input
            id={`edit-name-${team.teamId}`}
            name={`edit-name-${team.teamId}`}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        {/* `for`-linked, not wrapping — see CreateTeamForm. */}
        <div className="kombi-field">
          <label htmlFor={`edit-manager-${team.teamId}`}>Manager</label>
          {manager ? (
            <PersonPicker
              selected={manager}
              onPick={setManager}
              inputId={`edit-manager-${team.teamId}`}
            />
          ) : (
            <>
              <p className="kombi-field-hint">
                Currently <strong>{team.manager.displayName}</strong>. Pick a new manager to change
                (they must hold the Manager appointment), or leave as is.
              </p>
              <PersonPicker
                selected={null}
                onPick={setManager}
                inputId={`edit-manager-${team.teamId}`}
                placeholder="Search for a new manager…"
              />
            </>
          )}
        </div>
      </div>
      {error && <ErrorLine>{error}</ErrorLine>}
      <div className="kombi-admin-row-actions">
        <QuietAction onClick={onCancel} disabled={busy}>
          Cancel
        </QuietAction>
        <PrimaryAction
          onClick={() => void save()}
          disabled={busy || (!nameChanged && !managerChanged)}
        >
          {busy ? 'Saving…' : 'Save changes'}
        </PrimaryAction>
      </div>
    </div>
  );
}

/**
 * One team card. Collapsed by default into a compact CHIP — the summary line an admin/manager scans
 * (name, manager + email, roster count, champion count) — and expanded on click to reveal the full
 * editor (roster, add-member, rename/change-manager, delete). The chip header is a single toggle
 * button so the whole chip is the affordance; the management controls only mount once expanded, so a
 * long list of teams stays legible and nothing heavy renders until it is asked for.
 */
function TeamCard({ team, onChanged }: { team: TeamDetail; onChanged: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bodyId = `team-body-${team.teamId}`;
  const championSummary =
    team.champions.length > 0
      ? ` · ${team.champions.length} champion${team.champions.length === 1 ? '' : 's'}`
      : ' · no champion';

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      const res = await apiDelete<null>(`/api/admin/teams/${encodeURIComponent(team.teamId)}`);
      if (res.kind === 'ok') {
        onChanged();
        return;
      }
      setError(`Could not delete (${res.kind === 'error' ? res.status : 'unauthorized'}).`);
    } finally {
      setBusy(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <li>
      {/* The chip: the always-visible summary, and the toggle for the rest. One `.kombi-choice`, so
          the whole chip is clickable and carries Kombi's own hover and focus ring. `aria-expanded`
          alone announces the collapsed state — the chevron is decoration and no longer turns, because
          `.kombi-choice` paints only an `aria-pressed` state and this is a disclosure, not a toggle.
          The summary's three lines are `.kombi-admin-rows`' record grammar (a `strong` and its
          `small`s), which is what gives them their type and their line breaks. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={bodyId}
        className="kombi-choice"
      >
        <span className="kombi-admin-rows">
          <strong>
            {team.name} <span aria-hidden="true">›</span>
          </strong>
          <small>Manager: {team.manager.displayName}</small>
          <small>{team.manager.upn}</small>
          <small>
            {team.memberCount} on the roster{championSummary}
          </small>
        </span>
      </button>

      {expanded && (
        <div id={bodyId} className="kombi-dialog-stack">
          <div className="kombi-admin-row-actions">
            <QuietAction onClick={() => setEditing((v) => !v)}>
              {editing ? 'Close edit' : 'Edit'}
            </QuietAction>
            {confirmingDelete ? (
              <>
                <QuietAction onClick={() => setConfirmingDelete(false)} disabled={busy}>
                  Cancel
                </QuietAction>
                <QuietAction onClick={() => void remove()} disabled={busy}>
                  {busy ? 'Deleting…' : 'Confirm delete'}
                </QuietAction>
              </>
            ) : (
              <QuietAction onClick={() => setConfirmingDelete(true)}>Delete</QuietAction>
            )}
          </div>

          {error && <ErrorLine>{error}</ErrorLine>}

          {editing && (
            <EditTeamForm
              team={team}
              onSaved={() => {
                setEditing(false);
                onChanged();
              }}
              onCancel={() => setEditing(false)}
            />
          )}

          {team.members.length === 0 && team.champions.length === 0 ? (
            <EmptyState>No members yet. Add the first below.</EmptyState>
          ) : (
            <ul className="kombi-admin-rows">
              {team.champions.map((c) => (
                <RosterRow
                  key={c.userId}
                  teamId={team.teamId}
                  userId={c.userId}
                  displayName={c.displayName}
                  upn={c.upn}
                  roleLabel="Champion"
                  onRemoved={onChanged}
                />
              ))}
              {team.members.map((m) => (
                <RosterRow
                  key={m.userId}
                  teamId={team.teamId}
                  userId={m.userId}
                  displayName={m.displayName}
                  upn={m.upn}
                  roleLabel="Member"
                  onRemoved={onChanged}
                />
              ))}
            </ul>
          )}

          <AddMemberForm teamId={team.teamId} onAdded={onChanged} />
        </div>
      )}
    </li>
  );
}

/**
 * The overlay body AdminHome mounts. Fetches its own data on open (useOverlayData → teamsLoader), shows
 * the pending line (Spinner — words, not a ring) while loading, an ErrorLine for a failed load / ended
 * session, Kombi's own missing-state block (NotPermitted) for a forbidden result, and the create form +
 * team list on success — every mutation reload()s in place.
 */
export function TeamsPanel({
  onClose,
  returnFocusRef: _returnFocusRef,
}: {
  onClose: () => void;
  returnFocusRef: ReturnFocusRef;
}) {
  const { data, loading, error, reload } = useOverlayData(teamsLoader);

  return (
    <KombiAdminOverlay title="Teams" onClose={onClose}>
      {loading ? (
        <Spinner label="Loading teams…" />
      ) : error || (data && data.state === 'signedOut') ? (
        <ErrorLine>
          Your session has ended or the teams could not be loaded. Close this panel and sign in
          again.
        </ErrorLine>
      ) : data && data.state === 'forbidden' ? (
        <NotPermitted what="Teams" />
      ) : data && data.state === 'ok' ? (
        <div>
          <ProvisionalNote />
          <Notice>
            Build a team from a manager, ordinary members, and up to two champions. The manager must
            already hold the Manager appointment and a champion the ChampionRole appointment (grant
            those in Appointments) — a team records who holds a role, it never grants one. A person
            may belong to only one team, and a manager may manage only one team.
          </Notice>

          <SectionHeader>Create a team</SectionHeader>
          <CreateTeamForm onCreated={reload} />

          <SectionHeader>Teams</SectionHeader>
          {data.teams.length === 0 ? (
            <EmptyState>No teams yet. Create the first above.</EmptyState>
          ) : (
            <ul className="kombi-dialog-stack">
              {data.teams.map((t) => (
                <TeamCard key={t.teamId} team={t} onChanged={reload} />
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </KombiAdminOverlay>
  );
}

/**
 * Thin route wrapper for the registered /admin/teams deep link. Opens the panel always-open over a
 * blank backdrop; Close returns to /admin.
 */
export function Teams() {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement | null>(null);
  return <TeamsPanel onClose={() => navigate('/admin')} returnFocusRef={ref} />;
}
